import type { ShipmentEventId } from "@features/services/shipping/shipmentEvents";
import type { HOW_IT_WORKS_STEPS } from "@features/home/howItWorksSteps";

/**
 * Authored content for the detailed /how-it-works guide — everything here is
 * page-only prose. Facts that exist elsewhere in the product (rates, status
 * labels) are NOT duplicated here: the page imports them live from rates.ts
 * and shipmentEvents.ts so the guide can never drift from the app.
 */

export const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=233558239490";

type StepId = (typeof HOW_IT_WORKS_STEPS)[number]["id"];

export interface StepChecklistItem {
  label: string;
  hint?: string;
  optional?: boolean;
}

export interface StepExtras {
  checklist?: {
    title: string;
    items: StepChecklistItem[];
  };
  note?: string;
  links?: { label: string; href: string }[];
}

export const STEP_EXTRAS: Partial<Record<StepId, StepExtras>> = {
  quote: {
    note: "Your goods fall into one of four rate classes — Standard, Electronics & Fragile, Special Care, or Restricted — based on what you tell us you're shipping. If a shipment mixes categories, the whole shipment bills at the highest class present.",
    links: [{ label: "See the full rate table", href: "#rates" }],
  },
  label: {
    checklist: {
      title: "What you'll need",
      items: [
        { label: "Full name" },
        { label: "Phone number", hint: "we call this number before delivery" },
        { label: "Year of birth" },
        { label: "Area / town", hint: "e.g. Kasoa, Tema Community 18" },
        {
          label: "Directions & landmark",
          hint: "e.g. behind the blue kiosk after the old barrier",
        },
        {
          label: "Description of goods",
          hint: "shows your estimated rate as you type",
        },
        {
          label: "Exact GPS pin",
          hint: "one tap on “Use my current location”",
          optional: true,
        },
        {
          label: "Ghana Post GPS code",
          hint: "skip it if you don't know it",
          optional: true,
        },
      ],
    },
    note: "Submitting gives you a tracking number and a QR-code shipping label you can save as an image or print. Attach it to every carton.",
  },
  warehouse: {
    note: "Your label carries our Guangzhou warehouse address. Drop your goods off yourself, or have your supplier ship them there with the label attached — every package is logged against your tracking number as it arrives. Sea freight takes 35–50 days; air freight 5–10 days.",
  },
  billing: {
    note: "Your bill is measured CBM × your category's rate, with a 0.1 CBM minimum. It appears on your tracking page in USD with the Ghana cedi equivalent.",
    links: [
      { label: "Rates & billing rules", href: "#rates" },
      { label: "How to pay", href: "#payments" },
    ],
  },
  tracking: {
    links: [{ label: "What every status means", href: "#statuses" }],
  },
  delivery: {
    links: [{ label: "Questions? Message us on WhatsApp", href: WHATSAPP_URL }],
  },
};

/**
 * One plain-English sentence per tracking status. Typed against the full
 * event-id union so adding a new status to shipmentEvents.ts fails
 * compilation here until the guide explains it.
 */
export const STATUS_DESCRIPTIONS: Record<ShipmentEventId, string> = {
  REQUEST_SUBMITTED:
    "You've described your goods and asked to ship with us — we have your request.",
  LABEL_ISSUED:
    "Your tracking number and QR shipping label are ready to attach to your cargo.",
  CLIENT_CONTACTED:
    "Our team has reached out to confirm your shipment details.",
  CLIENT_REQUEST_CONFIRMED:
    "Everything's confirmed — we're expecting your goods at the warehouse.",
  SHIPPED_TO_GHANA:
    "Your package is on the vessel and sailing for Ghana.",
  ARRIVED_IN_GHANA:
    "The container has landed at the port in Ghana.",
  CLEARED_AND_INVENTORIED:
    "Customs is done and your package is checked into our local depot.",
  READY_FOR_DELIVERY:
    "Your package is ready — delivery is being scheduled.",
  OUT_FOR_DELIVERY:
    "A driver has your package and is on the way to your address.",
  DELIVERED: "Your package has been handed over. All done!",
};

export const BILLING_RULES = [
  "Minimum billable volume is 0.1 CBM — smaller parcels bill as 0.1.",
  "Mixed cargo bills at the highest rate class in the shipment.",
  "Raw batteries and paint at 1 CBM or more escalate to the Restricted rate.",
  "E-bikes, motorbikes and scooters are quoted per piece — message us on WhatsApp for a price.",
] as const;

export const PAYMENT_METHODS = [
  {
    title: "MTN MoMo or Alipay",
    description:
      "Pay your bill in Ghana cedis by MTN Mobile Money or Alipay, then message us on WhatsApp with your tracking number and the transaction reference so we can confirm receipt.",
  },
  {
    title: "Estimates before measurement",
    description:
      "Any figure shown before the warehouse measures your cargo — including one derived from your packing list — is indicative. Your final bill is set once we measure.",
  },
] as const;
